import Alert from "react-bootstrap/Alert";
import { resources } from "@constants/hardcoded-strings";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { AssertionAttribute } from "@idw-types/data/reference-data/generic";
import { FormattedRpServiceConfiguration, FormattedRpServiceConfigurationAttribute, FormattedRpServiceConfigurationServiceOffering, FormattedRpServiceConfigurationServiceOfferingAssertion } from "@idw-types/data/rp-service-configuration";
import { useMemo } from "react";
import { LabelValueContainer } from "@containers/common/label-value-container/label-value-container";
import styles from "../ap-assertions-tab-container.less";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";


type ApAssertionsMissingAttributesAlertProps = {
    rpServiceConfigurationState: FormattedRpServiceConfiguration;
    selectedAssertion: FormattedRpServiceConfigurationServiceOfferingAssertion;
    rpServiceConfigurationServiceOffering: FormattedRpServiceConfigurationServiceOffering;
    assertionAttributes: Array<AssertionAttribute>;
    isDisabled: boolean;
    selectedAttributes: Array<FormattedRpServiceConfigurationAttribute>;
    setRpServiceConfigurationServiceOffering: (serviceOffering: FormattedRpServiceConfigurationServiceOffering) => void;
};

export const ValidatedApAssertions = (props: ApAssertionsMissingAttributesAlertProps) => {
    const {
        selectedAssertion,
        rpServiceConfigurationState,
        selectedAttributes,
        assertionAttributes,
        rpServiceConfigurationServiceOffering,
        isDisabled,
        setRpServiceConfigurationServiceOffering
    } = props;

    const selectedAssertionAttributes = useMemo(() => {
        return assertionAttributes.filter(attribute => attribute.assertionID === selectedAssertion.assertionId);
    }, [assertionAttributes]);

    const attributeRelatedAssertions = useMemo(() => {
        const attributeRelatedAssertion = rpServiceConfigurationState.attributes?.filter(attr => selectedAssertionAttributes?.map(attribute => attribute.attributeTypeID).includes(attr.attributeType?.id));
        return attributeRelatedAssertion;
    }, [rpServiceConfigurationState.attributes, selectedAssertionAttributes]);

    const missingAttributes = useMemo(() => {
        const filteredAssertions = selectedAssertionAttributes.filter(assertion => !selectedAttributes.map(att => att.attributeType.id).includes(assertion.attributeTypeID))
        return rpServiceConfigurationState.attributes.filter(attr => filteredAssertions.map(assertion => assertion.attributeTypeID).includes(attr.attributeType.id));
    }, [selectedAssertionAttributes, selectedAttributes, rpServiceConfigurationState.attributes]);

    const hasMissingAttribute = useMemo(() => {
        if (attributeRelatedAssertions) {
            return !selectedAssertionAttributes.every(assertion => selectedAttributes.map(att => att.attributeType.id).includes(assertion.attributeTypeID))
        } else {
            return false
        }
    }, [attributeRelatedAssertions, selectedAssertionAttributes, selectedAttributes]);


    const warningMessage = useMemo(() => {
        const attributeString = missingAttributes.length > 1 ? "attributes" : "attribute";
        const attributeNames = missingAttributes?.map(attribute => attribute.templateName).join(', ').replace(/,(?!.*,)/gmi, ' and');
        const attributeNameString = `${attributeNames} ${attributeString}`;
        return `${resources.pages.verificationServiceDetails.tabs.assertions.subTabs.apAssertions.missingAttributesMessage(attributeNameString)}`;

    }, [missingAttributes]);


    const onClickRemoveAssertion = () => {
        const newAssertions = rpServiceConfigurationServiceOffering.assertions.map(assertion => {
            if (assertion.uuid === selectedAssertion.uuid) {
                if (assertion.id) {
                    return {
                        ...selectedAssertion,
                        status: EntityStatusTypes.INACTIVE
                    };
                } else {
                    return {
                        ...selectedAssertion,
                        addedToService: false,
                        status: EntityStatusTypes.INACTIVE
                    };
                }
            }

            return assertion;
        });

        setRpServiceConfigurationServiceOffering({
            ...rpServiceConfigurationServiceOffering,
            assertions: newAssertions
        });
    };

    return (
        <>
            <LabelValueContainer
                controlId={`ap-assertion-${selectedAssertion.apAssertionId}-label-value`}
                key={`ap-assertion-${selectedAssertion.apAssertionId}`}
                className={styles.assertionContainer}
                label={selectedAssertion.shortName}
                labelWidth={LabelWidthTypes.EXTRA_SMALL}
                value={selectedAssertion.description}
                layout={LayoutTypes.ROW}
                afterValueIcon={isDisabled ? null : faTrash}
                afterValueIconDisabled={isDisabled}
                afterValueIconOnClick={onClickRemoveAssertion}
                afterValueIconContainerMarginLeftAuto={true}
                labelIsBold={true}
            />
            {hasMissingAttribute && (
                <Alert variant={"warning"}>
                    {warningMessage}
                </Alert>)
            }
        </>

    );

};